/**
 * FileEmptyState Component
 * Empty state display for file lists with optional action
 */

import { forwardRef } from 'react';
import { Stack, Text, Button } from '@/components/ui';
import { Center } from '@/components/ui/layout';
import { IconFilesOff, IconUpload } from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';
import type { GradientVariant } from '@/components/ccms-components/types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface FileEmptyStateProps {
  /** Message to display */
  message?: string;
  /** Icon to display */
  icon?: Icon;
  /** Action button label */
  action?: string;
  /** Callback when action button is clicked */
  onActionClick?: () => void;
  /** Gradient variant for action button */
  gradientVariant?: GradientVariant;
  /** Additional className */
  className?: string;
}

/**
 * Empty state component for file and folder lists
 *
 * @example
 * ```tsx
 * <FileEmptyState
 *   message="No files found"
 *   icon={IconFilesOff}
 *   action="Upload Files"
 *   onActionClick={() => openUploadDialog()}
 * />
 * ```
 */
export const FileEmptyState = forwardRef<HTMLDivElement, FileEmptyStateProps>(
  (props, ref) => {
    const {
      message = 'No files found',
      icon: Icon = IconFilesOff,
      action,
      onActionClick,
      gradientVariant = 'primary',
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    return (
      <Center ref={ref} py="xl" className={className}>
        <Stack gap="lg" align="center" style={{ maxWidth: 400, textAlign: 'center' }}>
          {/* Icon */}
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-gray-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon
              size={40}
              color="var(--mantine-color-gray-5)"
              style={{ opacity: 0.6 }}
            />
          </div>

          {/* Message */}
          <Stack gap="xs" align="center">
            <Text size="lg" fw={600} c="dimmed" style={accessibilityStyles}>
              {message}
            </Text>
            {action && onActionClick && (
              <Text size="sm" c="dimmed" style={accessibilityStyles}>
                Get started by {action.toLowerCase()}
              </Text>
            )}
          </Stack>

          {/* Action Button */}
          {action && onActionClick && (
            <Button
              variant="light"
              gradientVariant={gradientVariant}
              size="md"
              onClick={onActionClick}
              leftSection={<IconUpload size={18} />}
            >
              {action}
            </Button>
          )}
        </Stack>
      </Center>
    );
  }
);

FileEmptyState.displayName = 'FileEmptyState';

export default FileEmptyState;
